import { Component, HostListener, ChangeDetectorRef, ViewChild, OnInit, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatListModule } from '@angular/material/list';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Router } from '@angular/router';
import { DashboardService } from '../services/dashboard.service';
import { CreateProjectService } from '../services/create-project.service';
import { NotificationService } from '../services/notification.service';
import { MatDialog } from '@angular/material/dialog';
import { RoleViewService } from '../services/role-view.service';
import { TutorialVideoStep, TutorialVideoWidgetComponent } from '../components/tutorial-video-widget/tutorial-video-widget.component';
import { ADMIN_TUTORIAL_FLOW } from '../components/tutorial-video-widget/tutorial-video-flows';
export interface Department {
  department_id: number;
  department_name: string;
  status?: string;
  sub_task_status?: string;
  assigned_person_name?: string;
  sub_task_updated_at?: string;
  sub_task_updated_by?: string;
  project_sub_task_id?: number;
}

export interface SubCollection {
  sub_collection_id: number;
  sub_collection_name: string;
}

export interface Project {
  project_id?: number;
  project_name: string;
  due_date: string;
  status: string;
  collection_id: string;
  collection_name: string;
  sub_collection_name?: string[];
  sub_collections?: SubCollection[];
  departments: Department[];
  contact_person?: string;
  activity?: string;
  project_created_at?: string;
  collection_documents?: any[];
}

export interface ActivityStream {
  activity_type: string;
  activity_description: string | null;
  user_comments: string | null;
  created_by: string;
  created_at: string;
  department_id: string;
  user_name: string | null;
}

type ConfirmType = 'edit' | 'delete' | 'copy';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCardModule,
    MatSidenavModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatListModule,
    TutorialVideoWidgetComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
  animations: [
    trigger('collapseExpand', [
      state('collapsed', style({
        height: '0',
        opacity: '0',
        overflow: 'hidden'
      })),
      state('expanded', style({
        height: '*',
        opacity: '1',
        overflow: 'visible'
      })),
      transition('collapsed <=> expanded', [
        animate('300ms ease-in-out')
      ])
    ])
  ]
})
export class DashboardComponent implements OnInit, AfterViewInit {
  showStatCards = true;
  private readonly IN_PROGRESS = 'In Progress';
  private readonly MANAGE_DOCUMENTS_ROUTE = '/manage-documents';

  readonly adminDashboardTutorialSequence: TutorialVideoStep[] = ADMIN_TUTORIAL_FLOW;
  
  projectCardDetails = [
    {
      title: 'Total Projects',
      value: 0,
      icon: 'work',
      color: 'blue-bg',
      titleValue: 'totalProjects',
    },
    {
      title: 'Draft',
      value: 0,
      icon: 'edit',
      color: 'blue-bg',
      titleValue: 'draft',
    },
    {
      title: 'Submitted',
      value: 0,
      icon: 'task_alt',
      color: 'blue-bg',
      titleValue: 'submitted',
    },
    {
      title: this.IN_PROGRESS,
      value: 0,
      icon: 'trending_up',
      color: 'blue-bg',
      titleValue: 'inProgress',
    },
    {
      title: 'Completed',
      value: 0,
      icon: 'check_circle',
      color: 'green-bg',
      titleValue: 'completed',
    },
    {
      title: 'Overdue',
      value: 0,
      icon: 'error',
      color: 'red-bg',
      titleValue: 'overdue',
    },
  ];
  filterDateOptions = [
    { label: 'All Records', value: '0' },
    { label: 'Last 7 Days', value: '7' },
    { label: 'Last 30 Days', value: '30' },
    { label: 'Last 60 Days', value: '60' },
    { label: 'Last 3 Months', value: '90' },
    { label: 'Last 6 Months', value: '180' },
  ];
  selectedDateFilter = '180';
  displayedColumns: string[] = [
    'expand',
    'project_id',
    'departments',
    'overallStatus',
    'project_created_at',
    'due_date',
    'collection_name',
    'sub_collection_name',
    'contact_person',
    'actions'
  ];
  isLoading = false;
  scrHeight = '';
  expandedElement: Project | null = null;
  showConfirmDialog = false;
  confirmType: ConfirmType | null = null;
  selectedProject: Project | null = null;

  projectGridData: Project[] = [];
  dataSource = new MatTableDataSource<Project>(this.projectGridData);
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  @ViewChild(MatSort) sort?: MatSort;
  @ViewChild('filterDrawer') filterDrawer!: any;
  
  departments: string[] = [];
  statuses: string[] = [];
  workDayId: any;
  userName = '';
  collections: string[] = [];

  selectedDepartments = new Set<string>();
  selectedStatuses = new Set<string>();
  selectedCollections = new Set<string>();

  showActivityStream = false;
  activityStreamType: 'main' | 'subtask' = 'main';
  newComment = '';
  isActivityLoading = false;
  activityStreamData: any[] = [];
  activityProjectId = '';
  activityDepartmentId = '';
  activityProjectSubTaskId = 0;

  filterValues = {
    search: '',
    departments: [] as string[],
    statuses: [] as string[],
    collections: [] as string[],
  };

  sortableColumns = [
    { id: 'project_id', label: 'Project ID' },
    { id: 'overallStatus', label: 'Overall Status' },
    { id: 'due_date', label: 'Due Date' },
    { id: 'collection_name', label: 'Collection' },
    { id: 'contact_person', label: 'Contact Person' },
    { id: 'activity', label: 'Activity' },
    { id: 'project_created_at', label: 'Created Date' },
  ];
  userRole = '';

  constructor(
    private readonly createProjectService: CreateProjectService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly dashboardService: DashboardService,
    private readonly notificationService: NotificationService,
    private readonly dialog: MatDialog,
    private readonly roleveService: RoleViewService
  ) {
    this.getScreenSize();
  }

  ngOnInit() { 
    this.workDayId = this.createProjectService.getWorkdayId();
    this.userName = this.dashboardService.getUserName();
    this.initializeDataSourceSettings();
    this.getDashboardData();
  }
  private initializeDataSourceSettings() {
    // Set up the filter predicate
    this.dataSource.filterPredicate = (data: Project, filter: string) => {
      const parsed = JSON.parse(filter || '{}');

      /* ---------------- SEARCH ---------------- */
      const search = (parsed.search || '').toLowerCase();

      const matchesSearch =
        !search ||
        data.project_name.toLowerCase().includes(search) ||
        data.project_id?.toString().toLowerCase().includes(search) ||
        data.collection_name?.toLowerCase().includes(search);

      /* -------------- DEPARTMENT -------------- */
      const deptMatch =
        !parsed.departments?.length ||
        data.departments?.some((dept) => parsed.departments.includes(dept.department_name));

      /* -------------- STATUS -------------- */
      const subTaskOnlyStatuses = new Set(['pending rre review', 'pending raes review']);
      const statusMatch =
        !parsed.statuses?.length ||
        parsed.statuses.some((status: string) => {
          const statusLower = status.toLowerCase();
          if (subTaskOnlyStatuses.has(statusLower)) {
            return data.departments?.some((dept) => (dept.sub_task_status || '').toLowerCase() === statusLower);
          }
          return (data.status || '').toLowerCase() === statusLower;
        });

      /* -------------- COLLECTION -------------- */
      const collectionMatch =
        !parsed.collections?.length ||
        parsed.collections.includes(data.collection_name);

      return matchesSearch && deptMatch && statusMatch && collectionMatch;
    };

    // Set up the sort data accessor
    this.dataSource.sortingDataAccessor = (data: Project, sortHeaderId: string): string | number => {
      switch (sortHeaderId) {
        case 'project_id':
          return data.project_id ?? '';
        case 'overallStatus':
          return data.status || '';
        case 'due_date':
          return data.due_date || '';
        case 'collection_name':
          return data.collection_name || '';
        case 'contact_person':
          return data.contact_person || '';
        case 'activity':
          return data.activity || '';
        case 'project_created_at':
          return data.project_created_at || '';
        default:
          return '';
      }
    };
  }

  
  goToProjectDetails(project: Project) {
    this.dashboardService.setSelectedProject(project);
    if (project.status?.toLowerCase() === 'draft') {
      this.router.navigate([this.MANAGE_DOCUMENTS_ROUTE]);
    } else {
      this.router.navigate(['/dashboard', project.project_id]);
    }
  }

  goToDepartmentDetails(_dept: any, project: Project) {
    if (project.status === 'Draft') {
      this.selectedProject = project;
      this.confirmType = 'edit';
      this.showConfirmDialog = true;
    } else {
      this.dashboardService.setSelectedProject(project);
      this.router.navigate(['/dashboard', project.project_id]);
    }
  }

  // scrHeight: string;
  // @HostListener('window:resize', ['$event'])
  // getScreenSize(event?: any) {
  //   this.scrHeight = window.innerHeight - 60 + 'px';
  // }

  
  @HostListener('window:resize', ['$event'])
  getScreenSize(_event?: any): void {
    this.scrHeight = `${window.innerHeight - 80}px`;
  }
  toggleSort(column: string) {
    if (!this.sort) {
      return;
    }

    if (this.sort.active === column) {
      this.sort.direction = this.sort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.sort.active = column;
      this.sort.direction = 'asc';
    }

    // Trigger the sort change which will automatically update the datasource
    this.sort.sortChange.emit({
      active: this.sort.active,
      direction: this.sort.direction,
    });
    
    // Force the datasource to re-sort
    this.dataSource.sort = this.sort;
  }

  getSortIcon(column: string): string {
    if (!this.sort?.active) {
      return '';
    }

    if (this.sort.active !== column) {
      return '';
    }

    return this.sort.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  applySort(column: string, direction: 'asc' | 'desc') {
    if (!this.sort) {return};
    this.sort.active = column;
    this.sort.direction = direction;
    this.sort.sortChange.emit({ active: column, direction });
  }
  toggleDepartment(dept: string) {
    this.toggleSet(this.selectedDepartments, dept);
  }

  toggleStatus(status: string) {
    this.toggleSet(this.selectedStatuses, status);
  }

  toggleCollection(col: string) {
    this.toggleSet(this.selectedCollections, col);
  }

  private toggleSet(set: Set<string>, value: string) {
    set.has(value) ? set.delete(value) : set.add(value);
  }

  applyCombinedFilter() {
    this.dataSource.filter = JSON.stringify(this.filterValues);

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
    this.updatePagination();
  }

  applySearch(event: Event) {
    this.filterValues.search = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();

    this.applyCombinedFilter();
  }

  reloadPage(): void {
    globalThis.location.reload();
  }

  initializeFilters() {
    
    const allDepartments: string[] = [];
    const allCollections: string[] = [];

    // Safely handle null or undefined data
    if (!this.dataSource.data || this.dataSource.data.length === 0) {
      this.departments = [];
      this.collections = [];
      this.statuses = [];
      return;
    }

    const projectStatuses: string[] = [];
    const subTaskStatuses: string[] = [];

    this.dataSource.data.forEach((project: Project) => {
      // Safely handle null or undefined departments
      if (project.departments && project.departments.length > 0) {
        project.departments.forEach((dept) => {
          if (dept.department_name) {
            allDepartments.push(dept.department_name);
          }
          const subTaskStatus = dept.sub_task_status;
          if (subTaskStatus) {
            const normalizedSubStatus = subTaskStatus.toLowerCase();
            if ((normalizedSubStatus === 'pending rre review' || normalizedSubStatus === 'pending raes review') &&
                !subTaskStatuses.some(s => s.toLowerCase() === normalizedSubStatus)) {
              subTaskStatuses.push(subTaskStatus);
            }
          }
        });
      }
      if (project.collection_name && !allCollections.includes(project.collection_name)) {
        allCollections.push(project.collection_name);
      }
      if (project.status && !projectStatuses.some(s => s.toLowerCase() === project.status.toLowerCase())) {
        projectStatuses.push(project.status);
      }
    });

    this.departments = Array.from(new Set(allDepartments)).filter(d => d != null).sort((a, b) =>
      a.localeCompare(b)
    );
    const orderedStatuses = ['Draft', 'Submitted', 'In Progress', 'Overdue', 'Pending RRE Review', 'Pending RAES Review', 'Completed'];
    this.statuses = orderedStatuses;
    this.collections = allCollections.filter(c => c != null).sort((a, b) => a.localeCompare(b));
  }

  getOverallStatus(project: Project): string {
    // Map the project status to display-friendly status
    const statusMap: { [key: string]: string } = {
      'DRAFT': 'Draft',
      'IN_PROGRESS': this.IN_PROGRESS,
      'COMPLETED': 'Completed',
      'PENDING_REVIEW': 'Pending Review',
      'OVERDUE': 'Overdue',
    };

    return statusMap[project.status] || 'Draft';
  }

  getCompletedDepartmentsCount(project: Project): { completed: number; total: number } {
    if (!project.departments || project.departments.length === 0) {
      return { completed: 0, total: 0 };
    }

    const completed = project.departments.filter((dept) => dept.status === 'COMPLETED').length;
    return { completed, total: project.departments.length };
  }

  updateProjectCardValues() {
    const totalProjects = this.projectGridData.length;
    const draftCount = this.projectGridData.filter(p => (p.status || '').toLowerCase() === 'draft').length;
    const submittedCount = this.projectGridData.filter(p => (p.status || '').toLowerCase() === 'submitted').length;
    const inProgressCount = this.projectGridData.filter(p => (p.status || '').toLowerCase() === this.IN_PROGRESS.toLowerCase()).length;
    const completedCount = this.projectGridData.filter(p => (p.status || '').toLowerCase() === 'completed').length;
    const overdueCount = this.projectGridData.filter(p => (p.status || '').toLowerCase() === 'overdue').length;

    this.projectCardDetails[0].value = totalProjects;
    this.projectCardDetails[1].value = draftCount;
    this.projectCardDetails[2].value = submittedCount;
    this.projectCardDetails[3].value = inProgressCount;
    this.projectCardDetails[4].value = completedCount;
    this.projectCardDetails[5].value = overdueCount;
  }

  checkAndUpdateOverdueStatus() {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to midnight for date comparison

    this.projectGridData.forEach((project) => {
      const statusLower = (project.status || '').toLowerCase();
      if (project.due_date && 
          statusLower !== 'completed' && 
          statusLower !== 'draft' && 
          statusLower !== 'overdue' &&
          statusLower !== 'approved') {
        // Parse as local midnight to avoid UTC offset shifting the date.
        const datePart = String(project.due_date).split('T')[0];
        const [y, m, d] = datePart.split('-').map(Number);
        const dueDate = new Date(y, m - 1, d);

        if (dueDate < today) {
          project.status = 'Overdue';
        }
      }
    });
  }

  ngAfterViewInit() {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }

    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
      this.paginator.page.subscribe(() => this.updatePagination());
    }
  }

  applyFilters() {
    this.filterValues.departments = [...this.selectedDepartments];
    this.filterValues.statuses = [...this.selectedStatuses];
    this.filterValues.collections = [...this.selectedCollections];

    this.applyCombinedFilter();
    this.filterDrawer.close();
  }

  clearFilters() {
    this.selectedDepartments.clear();
    this.selectedStatuses.clear();
    this.selectedCollections.clear();

    this.filterValues = {
      search: '',
      departments: [],
      statuses: [],
      collections: [],
    };

    this.applyCombinedFilter();
  }

  filterByStatus(statusKey: string) {
    // Map the card titles to the actual status values in the datasource
    const statusMapping: { [key: string]: string } = {
      'totalProjects': '', // Show all
      'draft': 'Draft',
      'submitted': 'Submitted',
      'inProgress': this.IN_PROGRESS,
      'completed': 'Completed',
      'overdue': 'Overdue',
    };

    const statusValue = statusMapping[statusKey];

    // Clear all filters first
    this.selectedStatuses.clear();

    // If totalProjects is clicked, clear all filters
    if (statusKey === 'totalProjects') {
      this.filterValues = {
        search: '',
        departments: [],
        statuses: [],
        collections: [],
      };
    } else {
      // Set the status filter to the selected status
      this.selectedStatuses.add(statusValue);
      this.filterValues.statuses = [...this.selectedStatuses];
    }

    this.applyCombinedFilter();
  }

  getStatusClass(status: string): string {
    return status === this.IN_PROGRESS ? 'status-progress' : 'status-draft';
  }

  pageCount = 0;
  pages: (number | string)[] = [];

  updatePagination() {
    if (!this.paginator) 
    {
      return;
    }

    // Use filteredData.length to respect current filters, fallback to total data length
    const length = this.dataSource.filteredData?.length || this.dataSource.data.length;
    this.paginator.length = length;

    this.pageCount = Math.ceil(length / this.paginator.pageSize);
    this.pages = this.generatePaginationPages();
  }

  private generatePaginationPages(): (number | string)[] {
    const MAX_PAGES = 10;
    const currentPage = (this.paginator?.pageIndex || 0) + 1;
    const pages: (number | string)[] = [];

    if (this.pageCount <= MAX_PAGES) {
      // Show all pages if 10 or fewer
      return Array.from({ length: this.pageCount }, (_, i) => i + 1);
    }

    // Show first page
    pages.push(1);

    // Calculate range around current page
    const rangeSize = 2; // Pages to show on each side of current page
    const startPage = Math.max(2, currentPage - rangeSize);
    const endPage = Math.min(this.pageCount - 1, currentPage + rangeSize);

    // Add left ellipsis if needed
    if (startPage > 2) {
      pages.push('...');
    }

    // Add page range around current page
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add right ellipsis if needed
    if (endPage < this.pageCount - 1) {
      pages.push('...');
    }

    // Show last page
    pages.push(this.pageCount);

    return pages;
  }

  goToPage(page: number) {
    if (!this.paginator)
    {
       return;
    }
    this.paginator.pageIndex = page;
    this.paginator._changePageSize(this.paginator.pageSize);
  }

  displayPageNumbers(): number[] {
    const MAX_DISPLAY = 5;
    const currentPage = (this.paginator?.pageIndex || 0) + 1;
    const pages: number[] = [];

    if (this.pageCount <= MAX_DISPLAY) {
      return Array.from({ length: this.pageCount }, (_, i) => i + 1);
    }

    // Calculate range around current page
    const rangeSize = 2;
    const startPage = Math.max(1, currentPage - rangeSize);
    const endPage = Math.min(this.pageCount, currentPage + rangeSize);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  goToPreviousPage() {
    if (!this.paginator) 
    {
      return;
    }
    this.paginator.previousPage();
  }

  goToNextPage() {
    if (!this.paginator) 
    {
      return;
    }
    this.paginator.nextPage();
  }

  getStartIndex(): number {
    if (!this.paginator) 
    {
      return 0;
    }
    return (this.paginator.pageIndex * this.paginator.pageSize) + 1;
  }

  getEndIndex(): number {
    if (!this.paginator) 
    {
      return 0;
    }
    const end = (this.paginator.pageIndex + 1) * this.paginator.pageSize;
    return Math.min(end, this.paginator.length);
  }

  onPageSizeChange(pageSize: any) {
    if (!this.paginator) 
    {
      return;
    }
    this.paginator.pageSize = Number.parseInt(pageSize, 10);
    this.paginator.firstPage();
    this.dataSource._updateChangeSubscription();
    this.cdr.detectChanges();
    this.updatePagination();
  }

  applyDateFilter(dateFilterValue: string) {
    this.selectedDateFilter = dateFilterValue;
    this.getDashboardData();
  }
  get startIndex(): number {
    if (!this.paginator) 
    {
      return 0;
    }
    return this.paginator.pageIndex * this.paginator.pageSize + 1;
  }

  get endIndex(): number {
    return this.getEndIndex();
  }
  getDashboardData(daysFilter: string = this.selectedDateFilter) {
    this.isLoading = true;
    this.dashboardService.fetchDashboardData(daysFilter).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.projectGridData = (response.payload || []).map((project: any) => ({
          ...project,
          project_id: project.project_details_id || project.project_id,
          project_created_at: project.project_created_at || project.created_at
        }));
        this.dataSource.data = [...this.projectGridData];
        this.checkAndUpdateOverdueStatus();
        this.updateProjectCardValues();
        this.initializeFilters();
      },
      error: (_error) => {
        this.isLoading = false;
      }
    }).add(() => {
      this.updatePagination();
    });
  }

  editProject(project: Project): void {
    this.selectedProject = project;
    this.confirmType = 'edit';
    this.showConfirmDialog = true;
  }

  deleteProject(project: Project): void {
    this.selectedProject = project;
    this.confirmType = 'delete';
    this.showConfirmDialog = true;
  }

  confirmYes(): void {
    if (this.confirmType === 'edit' && this.selectedProject) {
      this.dashboardService.setSelectedProject(this.selectedProject);
      this.router.navigate([this.MANAGE_DOCUMENTS_ROUTE]);
      this.router.navigate([this.MANAGE_DOCUMENTS_ROUTE, this.selectedProject.project_id]);
    } else if (this.confirmType === 'delete' && this.selectedProject) {
      this.deleteProjectDetails();
    } else if (this.confirmType === 'copy' && this.selectedProject) {
      this.dashboardService.setSelectedProject(this.selectedProject);
      this.router.navigate([this.MANAGE_DOCUMENTS_ROUTE, this.selectedProject.project_id], { queryParams: { mode: 'copy' } });
    }
    this.closeDialog();
  }

  deleteProjectDetails(): void {
    if (!this.selectedProject) 
    {
      return;
    }
    
    const projectName = this.selectedProject.project_name;
    const projectId = this.selectedProject.project_id;
    const request = { 
      projectId: Number(projectId),
      deletedBy: this.userName
    };
    
    this.dashboardService.deleteProjectDetails(this.workDayId, request).subscribe({
      next: (_response: any) => {
        this.projectGridData = this.projectGridData.filter(p => p.project_id !== projectId);
        this.dataSource.data = [...this.projectGridData];
        this.getDashboardData();
        this.showSuccessMsg(`Project "${projectName}" deleted successfully`);
      },
      error: (_error: any) => {
        this.showErrorMsg(`Failed to delete project "${projectName}". Please try again.`);
      }
    });
  }

  closeDialog(): void {
    this.showConfirmDialog = false;
    this.selectedProject = null;
    this.confirmType = null;
  }

  showSuccessMsg(msg: string): void {
    this.notificationService.showSuccess(msg);
  }

  showErrorMsg(msg: string): void {
    this.notificationService.showError(msg);
  }

  openActivityStream(type: 'main' | 'subtask', project: Project, dept?: Department): void {
    this.activityStreamType = type;
    this.showActivityStream = true;
    this.activityStreamData = [];
    this.isActivityLoading = true;
    this.isLoading = false;

    this.activityProjectId = project.project_id?.toString() || '';
    this.activityDepartmentId = dept?.department_id?.toString() || '';
    this.activityProjectSubTaskId = dept?.project_sub_task_id || 0;

    const departmentId = type === 'subtask' && dept ? dept.department_id?.toString() : undefined;

    this.dashboardService.fetchActivityStream(this.activityProjectId, departmentId).subscribe({
      next: (response) => {
        const data = response?.payload ?? response;
        this.activityStreamData = (Array.isArray(data) ? data : []) as ActivityStream[];
        this.isActivityLoading = false;
      },
      error: (_error) => {
        this.isActivityLoading = false;
      }
    });
  }

  confirmCopyDetails(project: Project): void {
    this.selectedProject = project;
    this.confirmType = 'copy';
    this.showConfirmDialog = true;
  }

  closeActivityStream(): void {
    this.showActivityStream = false;
    this.newComment = '';
  }

  postComment(): void {
    if (this.newComment.trim()) {
      const request: any = {
        projectId: this.activityProjectId,
        userComments: this.newComment,
        userName: this.userName
      };
      if (this.activityStreamType === 'subtask') {
        request.departmentId = this.activityDepartmentId;
        request.projectSubTaskId = this.activityProjectSubTaskId;
      }

      this.dashboardService.postActivityComments(this.workDayId, request).subscribe({
        next: (_response) => {
          const newActivity: ActivityStream = {
            activity_type: 'Comment',
            activity_description: null,
            user_comments: this.newComment,
            created_by: this.userName,
            created_at: new Date().toISOString(),
            department_id: this.activityDepartmentId,
            user_name: this.userName
          };
          
          this.activityStreamData.unshift(newActivity);
          this.newComment = '';
          this.showSuccessMsg('Comment added successfully');
        },
        error: (_error) => {
          this.showErrorMsg('Failed to add comment. Please try again.');
        }
      });
    }
  }

//   getInitials(name: string = ''): string {
//   const parts = name.trim().split(/\s+/).filter(Boolean);
//   if (!parts.length) return '?';
//   const first = parts[0]?.[0] ?? '';
//   const second = parts[1]?.[0] ?? '';
//   return (first + second).toUpperCase();
// }
}
